import { createContext, useCallback, useContext, useEffect, useState } from "react";
import API from "../lib/api";

const AuthContext = createContext(null);

// Roles that are valid for the admin panel
const ADMIN_PANEL_ROLES = ["ca", "admin", "super_admin"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // Uses the separate admin-panel auth endpoint (myca_admin_token cookie)
      const { data } = await API.get("/admin-auth/me");
      if (data && ADMIN_PANEL_ROLES.includes(data.role)) {
        setUser(data);
      } else {
        setUser(false);
      }
    } catch {
      setUser(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await API.post("/admin-auth/login", { email, password });
    if (!data || !ADMIN_PANEL_ROLES.includes(data.role)) {
      throw new Error("Access denied. This portal is for admin panel accounts only.");
    }
    // Note: we intentionally do NOT setUser(data) here. The sign-in page shows a
    // brief success confirmation, then hard-reloads to /admin where checkAuth
    // re-authenticates via the httpOnly cookie. Setting user now would flip
    // AuthRoute and unmount the sign-in page before the confirmation renders.
    return data;
  };

  const logout = async () => {
    await API.post("/admin-auth/logout");
    setUser(false);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  // Role helpers
  const isCA         = user?.role === "ca";
  const isAdmin      = user?.role === "admin";
  const isSuperAdmin = user?.role === "super_admin";
  const isAdminOrAbove    = isAdmin || isSuperAdmin;
  const isSuperAdminOnly  = isSuperAdmin;

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, refreshUser,
      isCA, isAdmin, isSuperAdmin,
      isAdminOrAbove, isSuperAdminOnly,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
